import React, {
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import Cobrowse from './Cobrowse';
import './GlanceStates.css';
import { baseurl } from './Glance.constants';
import { getSessionData, sessionInvite } from './session.service';
import { PresenceDetect } from './Presence.behavior';
import { AuthMethod, ErrorUserMessages } from './Agent.service';
import AgentPluginLayout from './components/AgentPluginLayout';
import ErrorMessage from './ui-components/ErrorMessage';
import EnterKeyState from './states/EnterKeyState';
import ErrorState from './states/ErrorState';
import CameraShareState from './states/CameraShareState';
import InSessionState from './states/InSessionState';
import PresenceState from './states/PresenceState';
import SessionlookupState from './states/SessionLookupState';
import WarningState from './states/WarningState';
import InitialState from './states/InitialState';
import LoadingState from './states/LoadingState';
import { State } from './State';

export interface HistoricalReportingData {
  sessionId: string;
  sessionStart: string;
  sessionEnd: string;
  duration: number;
}

interface GlanceProps {
  groupId: string;
  authUrl?: string;
  puid: string;
  openlocation?: string;
  iframeid?: string;
  gicon?: boolean;
  presence?: boolean;
  glanceBaseUrl?: string;
  visitorId?: string;
  debugMode?: boolean;
  customInvoke?: () => void;
  customOpenWindow : boolean;
  presession?: () => void;
  authHeaders?: Object;
  authBody?: URLSearchParams;
  authMethod : string;
  openWindow?: (url: string) => void;
  autoJoin?: boolean;
  errorMessage?: string;
  reportingCallback?: (sessionData: HistoricalReportingData) => void;
  reportingOrigin?: string;
  reportingExternalId?: string;
  uiVersion: number;
}

declare global {
  interface Window {
    // NOTE: we need to reference/set directly the GLANCE global var, not only GLANCE.Agent that is exposed by BA API
    GLANCE: any;
  }
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// NOTE: here Agent is the instance of AgentService or it's subclasses
let Agent: PresenceDetect;
let sessionStarted = false;
let openWindowCallback = null;
let inSession = false;
// NOTE: we've issues with state that is not updated, likely because of not using useRef - as a workaround, using variable
let sessionStart: number;

export const GlanceV2 = (props: GlanceProps) => {
  var defaultShowError = false;
  var defaultErrorMessage = '';
  if (props.errorMessage) {
    defaultShowError = true;
    defaultErrorMessage = props.errorMessage;
  }

  const alertTypes = {INFO: 'info', SUCCESS: 'success', WARNING: 'warning', ERROR: 'error'};
  
  const MIN_PHONE_LENGTH = 10;
  const MIN_KEY_LENGTH = 4;
  const [haveKey, setHaveKey] = useState(false);
  const [haveKeyDefault, setHaveKeyDefault] = useState(false);
  const [cobrowseKey, setCobrowseKey] = useState('');
  const [visitorId, setVisitorId] = useState('');
  const [agentJoinUrl, setAgentJoinUrl] = useState(null);
  //const [presenceEnabled, setPresenceEnabled] = useState(props.presence);
  //const [sessionEnd, setSessionEnd] = useState(Date.now());
  //const [phoneNumber, setPhoneNumber] = useState('');
  const [loadingCobrowse, setLoadingCobrowse] = useState(true);
  const [loadingMCS, setLoadingMCS] = useState(false);
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);
  const [showError, setShowError] = useState(defaultShowError);
  //const [mobileCameraShare, setMobileCameraShare] = useState(false);
  const [autoJoin, setAutoJoin] = useState(props.autoJoin || false);
  const [present, setPresent] = useState(false);
  let presenceIndicator = present; //Same as present, but independent from React.
  let switchFromEnterKeyToPresenceEnabled = false;
  const [alertSeverity, setAlertSeverity] = useState(alertTypes.ERROR);

  //Each state has two parts: a React component and an action to take when presence is detected.
  const statesMap = {
    EnterKey: new State("EnterKey",undefined, ()=>showPresenceStateIfApplicable()), //To be set after reading the settings.
    InSession: new State("InSession",<InSessionState gicon={props.gicon} onEndSession={()=>{ endSession({ reason: 'agent' }); }} />),
    SessionLookup: new State("SessionLookup",<SessionlookupState gicon={props.gicon} onCancel={()=>{ cancelSessionLookup() }}/>),
    CameraShare: new State("CameraShare",<CameraShareState gicon={props.gicon} onCancel={()=>{cancelMCS()}} onSend={(phone : string)=>{sendSMS(phone)}} minPhoneLength={MIN_PHONE_LENGTH}/>),
    Presence: new State("Presence",undefined,()=>showDefaultState()), //To be set when presence is detected.
    Initial: new State("Initial",<InitialState gicon={props.gicon} onEnterKey={()=>{showEnterKey()}} onMCS={()=>{showMCS()}}/>,()=>showDefaultState()),
    Loading: new State("Loading",<LoadingState gicon={props.gicon}/>,()=>showDefaultState())
  }
  const [currentState, setCurrentState] = useState(statesMap.Loading); 
  let stateIndicator = statesMap.Loading;//Like currentState, but independent from React.


/* Note: cobrowsePermissionFlag is a JavaScript variable, while cobrowsePermission is a React variable.
They should both have the same value at all times but, due to the way React works, either one of them can be 
temporarily set to false when it should be true. This happens to only one of them at a time, so we consider that
the permission is true when either one of them is true. It is an ugly workaround, but it works.

Same goes for mobileCameraPermissionFlag and mcsPermission.
 */

  let mobileCameraPermissionFlag: boolean;
  let cobrowsePermissionFlag = false;
  const [mcsPermission, setMcsPermission] = useState(false);
  const [cobrowsePermission, setCobrowsePermission] = useState(false);

  const handlePresence = (presentValue: boolean) => {
    setHaveKey(false);
    setCobrowseKey('');
    //setPhoneNumber('');
    setHaveKeyDefault(!presentValue && !mobileCameraPermissionFlag);
    setPresence(presentValue);
    stateIndicator.onPresenceChange();
  };

  const setPresence = function(presentValue : boolean) {
    presenceIndicator = presentValue;
    setPresent(presentValue);
  }

  const setState = function(state : State) {
    stateIndicator = state;
    setCurrentState(state);
  }

  const checkPermissions = () => {
    const mobileCameraPermission = {
      name: 'viewvideo',
      expectedValue: 'on',
      handler: (hasPermissionValue: boolean) => {
        console.log(`Inside checkPermissions.`);
        mobileCameraPermissionFlag = hasPermissionValue;
        setMcsPermission(mobileCameraPermissionFlag);
        setHaveKeyDefault(!mobileCameraPermissionFlag);
        setHaveKey(!mobileCameraPermissionFlag);
      },
    };
    const cobrowsePermission = {
      name: 'cobrowse',
      expectedValue: 'on',
      handler: (hasPermissionValue: boolean) => {
        cobrowsePermissionFlag = hasPermissionValue;
        setCobrowsePermission(hasPermissionValue);
        if (!hasPermissionValue) {
          setAutoJoin(false);
        }
        //setCobrowseEnabled(cobrowsePermissionFlag);
      },
    };
    const permissions = [mobileCameraPermission, cobrowsePermission];
    Agent.checkPermissions(permissions);
    setLoadingCobrowse(false);
    setLoadingMCS(false);
    enterKeyState();
    showDefaultState();
  };

  const enterKeyState = function() {
    if (statesMap.EnterKey.element == undefined) {
      const presenceEnabled = props.presence && props.visitorId != null && props.visitorId != "";
      statesMap.EnterKey.element = <EnterKeyState onCancel={()=>{cancelCobrowse()}} onJoin={(key : string)=>{joinCobrowse(key)}} minKeyLength={MIN_KEY_LENGTH} gicon={props.gicon} enableCancelWhenEmpty={mobileCameraPermissionFlag || presenceEnabled}/>;
    }
    return statesMap.EnterKey
  }

  const showDefaultState = function() { 
    console.log(`Inside showDefaultState, presenceIndicator = ${presenceIndicator}.`);
    setPresent(presenceIndicator);
    const cobrowseEnabled = cobrowsePermissionFlag||cobrowsePermission;
    const mcsEnabled = mobileCameraPermissionFlag || mcsPermission;
    setState((presenceIndicator && cobrowseEnabled) ? presenceState() : (mcsEnabled ? (cobrowseEnabled ? statesMap.Initial : statesMap.CameraShare) : enterKeyState()));
  }

  const presenceState = function() {
    if ((!cobrowsePermissionFlag)&&(!mobileCameraPermissionFlag)) {
      cobrowsePermissionFlag = true; //There was a problem refreshing the permissions.
    }
    if (statesMap.Presence.element == undefined) {
      statesMap.Presence.element = <PresenceState gicon={props.gicon} onEnterKey={()=>{showEnterKey()}} showMCS={mobileCameraPermissionFlag || mcsPermission} showEnterKey={cobrowsePermissionFlag} onMCS={()=>{showMCS()}} onJoin={()=>{startPresenceCobrowse()}}/>;
    }
    switchFromEnterKeyToPresenceEnabled = true;
    return statesMap.Presence;
  }

  const displayError = (message: string, hideError?: boolean, severity?: string) => {
    if (hideError) {
      setShowError(false);
      setErrorMessage('');
    } else {
      setAlertSeverity(severity ? severity : alertTypes.ERROR);
      setShowError(true);
      setErrorMessage(message);
    }
  };

  const showMCS = () => {
    setState(statesMap.CameraShare);
    clearErrorMessage();
  }

  const showEnterKey = () => {
    switchFromEnterKeyToPresenceEnabled = false;
    setState(enterKeyState());
    clearErrorMessage();
  }
  
  const showPresenceStateIfApplicable = () => {
    if (switchFromEnterKeyToPresenceEnabled) {
      showDefaultState()
    }
  }

  const prevProps: GlanceProps = usePrevious(props);

  const createAgent = async () => {
    // NOTE: we don't have glance environment set on the BA API instance level, so we're setting relevant global var for cobrowse/presence
    window.GLANCE.Agent.webserver = `https://${baseurl[props.glanceBaseUrl]}`;

    // NOTE: intentionally doing an invalidation of the token in a specific way due to bugs / workarounds in BA API
    window.GLANCE.Authorization.setToken(true); // setting token value to true so that authorized() will return true
    await window.GLANCE.Authorization.authorize({}); // called directly to initialize _ls and skip actual token obtaining, with empty params required
    window.GLANCE.Authorization.clearToken(); // clearToken works here because _ls is initialized, otherwise - not (due to _ls is undefined until the presenceConnect is called)

    Agent = new PresenceDetect(
      {
        url: props.authUrl,
        method: props.authMethod,
        groupId: props.groupId,
        puid: props.puid,
        headers: props.authHeaders,
        body: props.authBody,
        debug: props.debugMode,
        errorHandler: displayError,
      },
      {
        visitorId: props.visitorId,
        presenceCallback: handlePresence,
        permissionsCallback: checkPermissions,
        debug: props.debugMode,
        endSessionCallback: (val) => {
          return endSession(val);
        },
        startSessionCallback: (key) => startCobrowse(key),
      }
    );
    Agent.connect(); 
  };

  if (
    prevProps &&
    prevProps.visitorId &&
    prevProps.visitorId !== props.visitorId
  ) {
    if (props.visitorId !== visitorId) {
      console.log(`Visitor ID change: ${props.visitorId} != ${visitorId}.`);
      setVisitorId(props.visitorId);
      setPresence(false);
    }

    Agent.updateVisitorId(props.visitorId);
  }
  console.log(`Props: ${JSON.stringify(props)}`);
  if (prevProps !== props) {
    if (
      (props.authMethod === AuthMethod.SamlWithLoginkey && props.groupId) ||
      (props.authMethod === AuthMethod.Saml && props.groupId) ||
      (props.authMethod === AuthMethod.Loginkey && props.puid && props.authUrl)
    ) {
      if (props.debugMode) {
        console.log('GLANCE: Agent Plugin loading with:', {
          visitorid: props.visitorId,
          puid: props.puid,
          authurl: props.authUrl,
          openwindow: props.openWindow,
          glancebaseurl: props.glanceBaseUrl,
        });
      }
      createAgent();
    }
    openWindowCallback = props.openWindow;
  }

  useEffect(() => {
    console.log(present ? `Visitor is present.` : `Visitor is not present.`);
    if (present && autoJoin) {
      presenceIndicator = true;
      joinCobrowse(""); //There is no key, we're using presence.
    }
  }, [present, autoJoin]);

  // useEffect(() => {
  //   // createAgent();
  // }, []);

  // useEffect(() => {}, [
  //   present,
  //   haveKey,
  //   cobrowseKey,
  //   agentJoinUrl,
  //   loadingCobrowse,
  //   loadingMCS,
  // ]);

  //Stops session lookup. 
  //This assumes that there can only be one session at a time (we cannot have cobrowse and MCS at the same time).
  const cancelSessionLookup = async() => {
    // cancel session flow
    if (props.presence) {
      switchFromEnterKeyToPresenceEnabled = true;
      Agent.lookupVisitor();
    }
    setAutoJoin(false);
    setLoadingCobrowse(false);
    setLoadingMCS(false); 
    clearErrorMessage();
    cancelSession({ reason: 'agent' });
  }
  
const initializeCobrowse = function() {
  sessionStart = Date.now();
  sessionStarted = true;
  setLoadingCobrowse(true);
  setState(statesMap.SessionLookup);
  clearErrorMessage();
}

  // TODO: we need to refactor this - join that is responsible for everything should be reworked
  const joinCobrowse = async (key : string) => {
    console.log(`Inside joinCobrowse with key=${key}.`);
    setCobrowseKey(key); //key and cobrowseKey are the same, but key is used within this function and cobrowseKey is used outside (to end the session).
    setState(statesMap.SessionLookup);
    let keyNotBlank = (key != undefined && key != null && key.trim() != ""); //This is the same as haveKey, but it is set faster so we can use it immediately.
    setHaveKey(keyNotBlank);
    if (!present && !keyNotBlank) {
      setHaveKey(true);
    } else if (haveKeyDefault || keyNotBlank) {
      initializeCobrowse();
      await startCobrowse(key);
    } else if (props.visitorId && present) {
      startPresenceCobrowse();
    }
    // if (loadingCobrowse) {
    //   setShowInfo(true);
    //   setInfoMessage('Waiting for visitor');
    // }
  };

  const startPresenceCobrowse = async function() {
    initializeCobrowse();
    const visitorId = props.visitorId;
    const acceptTermsHandler = () => {
      if (sessionStarted) {
        clearErrorMessage();
        setAutoJoin(false);
      }
    };
    const declineTermsHandler = () => {
      if (sessionStarted) {
        setLoadingCobrowse(false);
        sessionStarted = false;
        setAutoJoin(false);
        showDefaultState();
        displayError(ErrorUserMessages.PresenceDeclinedError, false, alertTypes.INFO);
      }
    };
    await Agent.presenceStartSession(
      visitorId,
      acceptTermsHandler,
      declineTermsHandler
    );
  }

  const resolution = ''; //'352x288'
  const sendSMS = async (number : string) => {
    console.log(`Inside sendSMS. Number: ${number}`);
    //setPhoneNumber(number);
    setState(statesMap.SessionLookup);
    clearErrorMessage();
    if (loadingMCS) {
      console.log(`loadingMCS.`);
      setLoadingMCS(false);
    } 
    else if (number.length >= MIN_PHONE_LENGTH) {
      console.log(`Valid number.`);
      sessionStart = Date.now();
      setLoadingMCS(true);
      sessionStarted = true;
      try {
        const invite = await sessionInvite(
          {
            baseUrl: baseurl[props.glanceBaseUrl],
            groupId: Agent.groupId,
            puid: Agent.puid,
            loginKey: Agent.loginKey,
          },
          {
            sessionType: 1,
            smsPhone: number,
            agentName: Agent.puid,
            sessionParams: {
              camera: 'back',
              resolution: resolution,
            },
          }
        );
        await startCobrowse(invite.sessionKey);
      } catch (error) {
        setState(statesMap.CameraShare);
        clearErrorMessage();
        if (error?.name == 'ExternalServiceHandledError') {
          const userMessage =
            ErrorUserMessages.InviteSessionBusinessError.replace(
              '{0}',
              error.message
            );
          Agent.defaultErrorHandler(error, 'sendSMS', userMessage);
        } else {
          Agent.defaultErrorHandler(
            error,
            'sendSMS',
            ErrorUserMessages.InviteSessionError
          );
        }
      }
    }
    else {
      console.log(`Invalid phone number.`);
    }
  };

  const cancelSession = (cancelSessionReason: any) => {
    // TODO: potentially we need to support different reasons of cancel - like timeout, agent initiated etc
    if (sessionStarted) {
      Agent.lookupServiceCancel();
      // NOTE: historical data - reporting flow, starts only with session end
      if (props.reportingCallback && Agent.sessionId) {
        const sessionEndTime = Date.now();
        //setSessionEnd(sessionEndTime);
        const durationInSeconds = Math.floor(
          (sessionEndTime - sessionStart) / 1000
        );
        const sessionData: HistoricalReportingData = {
          sessionStart: sessionStart ? new Date(sessionStart).toISOString() : "UNKNOWN",
          sessionEnd: sessionEndTime ? new Date(sessionEndTime).toISOString() : "UNKNOWN",
          duration: durationInSeconds,
          sessionId: Agent.sessionId.toString(),
        };

        // TODO: implement session data from db
        // const sessionData = await getSessionData({
        //   baseUrl: `${props.glancebaseurl}`,
        //   groupId: props.groupid as string,
        //   apiKey: props.puid,
        //   visitorId: props.visitorid,
        //   start: sessionStart,
        //   end: sessionEnd,
        //   puid: props.puid,
        // });

        props.reportingCallback(sessionData);
      }
      console.log(`Inside cancelSession.`);
      inSession = false;
      setCobrowseKey('');
      //setPhoneNumber('');
      setAgentJoinUrl(null);
      setHaveKey(false);
      setLoadingMCS(false);
      setLoadingCobrowse(false);
      Agent.resetSessionId();
      Agent.cancelEndSessionCheck();
      sessionStarted = false;
    }
    showDefaultState();
  };

  const endSession = async (endSessionReason: any) => {
    // TODO: potentially we need to support different reasons of cancel - like agent initiated, visitor initiated, error event etc
    if (sessionStarted) {
      inSession = false;
      // NOTE: scenario when we hold the Agent window reference, we can post message or close the it
      if (Agent.hasAgentWindow()) {
        // NOTE: we're just closing window now if we've reference to it - but we can do a "right" postMessage(s) and keep window opened
        const windowClosed = Agent.closeAgentWindow();
      } else if (props.authMethod == AuthMethod.Loginkey) {
        // NOTE: the scenario when we don't have windows references and have server auth inplace
        if (props.visitorId && present) {
          await Agent.presenceEndSession(props.visitorId);
        } else if (!present && haveKey && cobrowseKey.length > 0) {
          await Agent.cobrowseEndSession(cobrowseKey);
        }
      }
      cancelSession(endSessionReason);
    }
  };

  const startCobrowse = async (key: string) => {
    if (sessionStarted) {
      // TODO: Agent.js does not support tab (bug) so we cannot use joinSession yet
      // TODO: We also cannot use joinSession if customers require passing in an openWindow callback

      const agentJoinUrlRaw = await Agent.lookupService({
        cobrowseKey: key,
        debug: props.debugMode,
        sessionStateHandler: (isActiveSession) => {
          const reason = { reason: 'agent' };
          if (isActiveSession && !inSession) {
            onSessionStarted();//This sets inSession to true and the current state to inSession.
          } else if (!isActiveSession && (agentIsInSession() || loadingCobrowse)) {
            cancelSession(reason);//This goes back to the default state even if there was no session to cancel.
          }
          if (!isActiveSession) {
            Agent.cancelEndSessionCheck();
          }
        },
      });

      // NOTE: we're intentionally double-checking here the startSession because session can be canceled already and we've not event-based here
      if (sessionStarted) {
        if (!agentJoinUrlRaw) {
          //setShowError(true);
          setLoadingCobrowse(false);
          clearErrorMessage();
          setState(errorState(ErrorUserMessages.LookupSessionNotFoundError));
        } else {
          if (props.debugMode) {
            console.log(
              `GLANCE: opening ${props.openlocation} for session key: ${key}`
            );
          }
          const reportingOriginKey = 'origin';
          const reportingExternalIdKey = 'xid';
          const agentJoinUrlWithParams = new URL(agentJoinUrlRaw);
          agentJoinUrlWithParams.searchParams.set(reportingOriginKey, props.reportingOrigin);
          agentJoinUrlWithParams.searchParams.set(reportingExternalIdKey, props.reportingExternalId);
          const agentJoinUrl = agentJoinUrlWithParams.toString();
          switch (props.openlocation) {
            case 'iframe':
              setAgentJoinUrl(agentJoinUrl);
              break;
            case 'tab':
              if (openWindowCallback) openWindowCallback(agentJoinUrl);
              else Agent.openWindow(agentJoinUrl, '_blank', cancelSession);
              break;
            default:
              if (openWindowCallback) openWindowCallback(agentJoinUrl);
              else
                Agent.openWindow(
                  agentJoinUrl,
                  'width=800,height=800',
                  cancelSession
                );
          }

          setLoadingCobrowse(false);
          setLoadingMCS(false);
          onSessionStarted();
        }
      }
    }

  };

  const agentIsInSession = function() {
    return inSession || currentStateIs(statesMap.InSession);
  }

  const errorState = function(message:string): State {
    return new State("Error", <ErrorState buttonText={"Go back"} errorMessage={message} gicon={props.gicon} onClick={() => { showDefaultState(); } } />);
  }

  const onSessionStarted = function() {
    inSession = true;
    setState(statesMap.InSession);
    clearErrorMessage();
  }

  const cancelCobrowse = () => {
    console.log(`Inside cancelCobrowse.`);
    switchFromEnterKeyToPresenceEnabled = true;
    setCobrowseKey('');
    setHaveKey(false);
    setLoadingCobrowse(false);
    clearErrorMessage();
    showDefaultState();
    clearTextInput();
  };

  const cancelMCS = () => {
    console.log(`Inside cancelMCS.`);
    //setPhoneNumber('');
    setLoadingMCS(false);
    setState(statesMap.Loading);//To clear the text input.
    clearErrorMessage();
    showDefaultState();
    clearTextInput();
  };

  const clearTextInput = () => {
    let textInput = document.getElementById('TextInput');
    if (textInput) {
      //@ts-ignore
      textInput.value = "";
    }
  }

  function clearErrorMessage() {
    setShowError(false);
    setErrorMessage('');
  }
  const currentStateIs = function (state: State) {
      return currentState.name == state.name;
  }

  return (
    <AgentPluginLayout>
      {agentJoinUrl ? (
        <Cobrowse url={agentJoinUrl} />
      ) : (
        <>
          {currentState.element}
          <ErrorMessage
            message={errorMessage}
            severity={alertSeverity}
            display={showError}
            onClose={clearErrorMessage}
          />
        </>
      )}
    </AgentPluginLayout>
  );
};
